import React, { useEffect, useState } from "react"
import { Button, Card, Collapse, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { toast } from "react-toastify"

import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  HasModulePermission,
  useHandleUnauthorizedAccess,
} from "../../../lib/GlobalMethods"
import { modulePermission } from "../../../lib/GlobalVariables"
import useDebounce from "../../../lib/hooks/useDebounce"
import FleetClientManagementSkeleton from "../../../skeleton/fleetManagement/fleetClientManagement"
import AddEditFleetVendor from "./AddEditFleetVendor"

const FleetVendorManagement = () => {
  const canAdd = HasModulePermission(modulePermission.fleetManagement, "add")
  const canEdit = HasModulePermission(modulePermission.fleetManagement, "edit")
  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [statusTarget, setStatusTarget] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: false })
  const [recordsCount, setRecordsCount] = useState({
    total_records: 0,
    active_records: 0,
    inactive_records: 0,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const { handleAccess } = useHandleUnauthorizedAccess()

  const resetList = () => {
    setData([])
    setPagination({ current_page: 1, total_pages: false })
    setRefreshKey((value) => value + 1)
  }

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setStatusTarget(null)
  }

  const fetchData = async (search = debouncedSearchQuery) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.fleetVendorList}?page=${pagination.current_page}&limit=10&search_term=${encodeURIComponent(search || "")}${statusFilter ? `&status=${statusFilter}` : ""}`,
      })

      handleAccess({ code: response?.data?.code })
      if (response?.data?.code !== 200) {
        throw new Error(response?.data?.message || "Unable to load Fleet Vendors")
      }

      const fetchedData = response.data.data || []
      setPagination(response.data.pagination || {})
      setRecordsCount(response.data.record_counts || recordsCount)
      setHasMore(Boolean(fetchedData.length) && response?.data?.pagination?.current_page !== response?.data?.pagination?.total_pages)
      setData((previousData) =>
        pagination.current_page === 1 ? fetchedData : [...previousData, ...fetchedData],
      )
    } catch (error) {
      toast.error(error?.message || "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
    // fetchData intentionally follows the existing paginated list pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, statusFilter, pagination.current_page, refreshKey])

  const changeStatus = async () => {
    if (!statusTarget) return
    try {
      setLoading(true)
      const nextStatus = statusTarget.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateFleetVendorStatus + statusTarget.id,
        data: { status: nextStatus },
      })

      if (response?.data?.code !== 200) {
        throw new Error(response?.data?.message || "Unable to update status")
      }

      toast.success(response.data.message)
      handleClose()
      resetList()
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
      setLoading(false)
    }
  }

  const setFilter = (status) => {
    setStatusFilter(status)
    setData([])
    setPagination({ current_page: 1, total_pages: false })
  }

  return (
    <>
      <PageTitleButton
        PagetitleText="Fleet Vendor Management"
        {...(canAdd
          ? {
              HeaderButtonText: "Add Fleet Vendor",
              IconclassName: "plus_black",
              onClick: () => {
                setIsUpdate(false)
                setShow(true)
              },
            }
          : {})}
        filterShow={true}
        filterClick={() => setOpen(!open)}
        open={open}
      />

      <div className="details_cards_wrapper mb-3">
        {[
          ["", "bg_sea_green", "Total Fleet Vendors", recordsCount.total_records],
          ["ACTIVE", "bg_light_green", "Active Fleet Vendors", recordsCount.active_records],
          ["INACTIVE", "bg_light_red", "Inactive Fleet Vendors", recordsCount.inactive_records],
        ].map(([filter, className, label, count]) => (
          <div
            key={label}
            className={`${className} details_card ${statusFilter === filter ? "card_active" : ""}`}
            onClick={() => setFilter(filter)}
          >
            <div className="card_titleicon"><h3 className="card_title">{label}</h3></div>
            <p className="tls_numbers">{count || 0}</p>
          </div>
        ))}
      </div>

      <Collapse in={open}>
        <div id="fleet-vendor-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <SearchBox
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value)
                  setData([])
                  setPagination({ current_page: 1, total_pages: false })
                }}
              />
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card><Card.Body>
          <InfiniteScroll
            dataLength={data.length}
            next={() => setPagination((value) => ({ ...value, current_page: value.current_page + 1 }))}
            hasMore={hasMore}
            loader={<FleetClientManagementSkeleton infiniteScroll={true} rows={1} />}
          >
            <Table className="rounded mb-0 table-striped" responsive="md">
              <thead><tr>
                <th>Vendor ID</th><th>Vendor Name</th><th>Contact No.</th><th>Primary Fleet Client</th>
                <th>Cost per kWh (₹)</th>
                <th>Status</th>{canEdit && <th className="text-center">Action</th>}
              </tr></thead>
              <tbody>
                {data.length ? data.map((vendor) => (
                  <tr key={vendor.id}>
                    <td>{vendor.id}</td>
                    <td>{vendor.name}</td>
                    <td>{vendor.contact_no || "—"}</td>
                    <td>{vendor.primary_fleet_client?.name || "—"}</td>
                    <td>{vendor.cost_per_kwh != null ? `₹${vendor.cost_per_kwh}/kWh` : "—"}</td>
                    <td>{vendor.status}</td>
                    {canEdit && <td className="text-center"><div className="d-inline-flex gap-2">
                      <TooltipComponent toolplace="top" tooltipText="Edit">
                        <Button variant="btnedit_icon" className="btn_icon" onClick={() => { setIsUpdate(vendor); setShow(true) }}>
                          <i className="icon_main edit_pencil"></i>
                        </Button>
                      </TooltipComponent>
                      <TooltipComponent
                        toolplace="top"
                        tooltipText={vendor.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      >
                        <Button
                          variant={vendor.status === "ACTIVE" ? "btnarchive_icon" : "btnrestore_icon"}
                          className="btn_icon"
                          onClick={() => setStatusTarget(vendor)}
                        >
                          <i
                            className={`icon_main ${
                              vendor.status === "ACTIVE"
                                ? "archive_data"
                                : "restore_data"
                            }`}
                          ></i>
                        </Button>
                      </TooltipComponent>
                    </div></td>}
                  </tr>
                )) : (
                  <tr><td className="text-center py-4" colSpan={canEdit ? 7 : 6}>
                    <div className="norecord_wrapper"><img src={recordNotFound} alt="No Record Found" /><p className="norecords_title">No Records Found</p></div>
                  </td></tr>
                )}
              </tbody>
            </Table>
          </InfiniteScroll>
        </Card.Body></Card>
      ) : <FleetClientManagementSkeleton />}

      <AddEditFleetVendor
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={resetList}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
      />

      <SweetAlerts
        showAlert={Boolean(statusTarget)}
        loading={loading}
        alertMessage={`Are you sure you want to ${statusTarget?.status === "ACTIVE" ? "deactivate" : "activate"} this Fleet Vendor?`}
        handleClose={handleClose}
        iconClassName={
          statusTarget?.status === "ACTIVE"
            ? "archive_data bg-warning"
            : "restore_data bg-black"
        }
        handleAlert={changeStatus}
      />
    </>
  )
}

export default FleetVendorManagement
